/**
 * The list detail page's filter bar (Trello #831), laid out like People Lists'
 * detail page (`ReviewStatusFilterBar` above `ContactsTable`'s filter line):
 *
 *   [All (N)]  We know [Any ▾]  LinkedIn [Any ▾]
 *   [Search by name or website…]  Employees: From – To  Revenue ($M): From – To
 *
 * The `All` pill is People Lists' semaphore and reset: lit when nothing
 * filters, a click clears everything, and it carries the unfiltered count.
 * The selects and the pill share `ReviewStatusFilterBar`'s classes; the boxes
 * share `filterInputClass`, as every filter box on People Lists does.
 *
 * Revenue is in MILLIONS of USD, the unit the Revenue column is built from
 * (`formatUsdMillions` shows 15.5 as "$15.5M"), and the label says so.
 *
 * Presentational: state, debounce and the page reset live in
 * `useCompanyMembersFilters`.
 */
import {
  allPillClass,
  selectClass,
} from "../../routes/research/import-detail/ReviewStatusFilterBar";
import { filterInputClass } from "../../routes/research/import-detail/filterInputClass";
import type {
  CompanyMembersFilters,
  LinkedinChoice,
  MemberTextField,
  WeKnowChoice,
} from "./useCompanyMembersFilters";

interface CompanyMembersFilterBarProps {
  filters: CompanyMembersFilters;
  /** Every company on the list, for the `All` pill; absent while loading. */
  total?: number;
}

function RangeInputs({
  label,
  noun,
  from,
  to,
  filters,
  step,
}: {
  label: string;
  noun: string;
  from: MemberTextField;
  to: MemberTextField;
  filters: CompanyMembersFilters;
  step: string;
}) {
  const box = `w-24 ${filterInputClass}`;
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-label text-text-secondary">{label}</span>
      <input
        type="number"
        min={0}
        step={step}
        inputMode="decimal"
        aria-label={`${noun} from`}
        placeholder="From"
        data-testid={`company-filter-${from}`}
        value={filters.drafts[from]}
        onChange={(e) => filters.setDraft(from, e.target.value)}
        className={box}
      />
      <span className="text-text-muted" aria-hidden="true">
        –
      </span>
      <input
        type="number"
        min={0}
        step={step}
        inputMode="decimal"
        aria-label={`${noun} to`}
        placeholder="To"
        data-testid={`company-filter-${to}`}
        value={filters.drafts[to]}
        onChange={(e) => filters.setDraft(to, e.target.value)}
        className={box}
      />
    </div>
  );
}

export function CompanyMembersFilterBar({ filters, total }: CompanyMembersFilterBarProps) {
  const draftsEmpty = Object.values(filters.drafts).every((v) => !v.trim());
  const nothingFiltered = !filters.active && draftsEmpty;
  return (
    <div className="flex flex-col gap-3" data-testid="company-members-filters">
      <fieldset className="m-0 flex min-w-0 flex-wrap items-center gap-1.5 border-0 p-0">
        <legend className="sr-only">Filter companies</legend>
        <button
          type="button"
          aria-pressed={nothingFiltered}
          data-testid="company-filter-all"
          onClick={() => {
            if (!nothingFiltered) filters.reset();
          }}
          className={allPillClass(nothingFiltered)}
        >
          {total === undefined ? "All" : `All (${total.toLocaleString()})`}
        </button>
        <label className="flex items-center gap-1.5 text-label text-text-secondary">
          We know
          <select
            data-testid="company-filter-we-know"
            value={filters.weKnow}
            onChange={(e) => filters.setWeKnow(e.target.value as WeKnowChoice)}
            className={selectClass(filters.weKnow !== "any")}
          >
            <option value="any">Any</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </label>
        <label className="flex items-center gap-1.5 text-label text-text-secondary">
          LinkedIn
          <select
            data-testid="company-filter-linkedin"
            value={filters.linkedin}
            onChange={(e) => filters.setLinkedin(e.target.value as LinkedinChoice)}
            className={selectClass(filters.linkedin !== "any")}
          >
            <option value="any">Any</option>
            <option value="matched">Matched</option>
            <option value="not_matched">Not matched</option>
          </select>
        </label>
      </fieldset>

      <div className="flex flex-row flex-wrap items-center gap-3">
        <input
          type="text"
          data-testid="company-filter-q"
          aria-label="Search companies by name or website"
          placeholder="Search by name or website…"
          value={filters.drafts.q}
          onChange={(e) => filters.setDraft("q", e.target.value)}
          className={`w-80 ${filterInputClass}`}
        />
        <RangeInputs
          label="Employees:"
          noun="Employees"
          from="employeesMin"
          to="employeesMax"
          filters={filters}
          step="1"
        />
        <RangeInputs
          label="Revenue ($M):"
          noun="Revenue in millions of USD"
          from="revenueMin"
          to="revenueMax"
          filters={filters}
          step="any"
        />
      </div>
    </div>
  );
}
